import classNames from 'classnames';
import { forwardRef } from 'react';
import Link from '@/shared/components/shared/Link';
import { absoluteFromHost } from '@/shared/lib/utils/absolute-url';
import Typografed from '../Typografed';

export type AppBreadcrumbs = { text: string; href?: string | null }[];

type Props = React.HTMLAttributes<HTMLElement> & {
    breadcrumbs: AppBreadcrumbs;
    className?: string;
};

const Breadcrumbs = forwardRef<HTMLDivElement, Props>(({ breadcrumbs, ...props }, ref) => {
    const host = (process.env.NEXT_PUBLIC_HOST || '').trim();
    return breadcrumbs.length > 0 ? (
        <nav
            {...props}
            ref={ref}
            className={classNames('breadcrumbs', props.className)}
            itemScope
            itemType="http://schema.org/BreadcrumbList"
        >
            <div className="breadcrumbs-inner">
                <ol className="breadcrumbs__list list-unstyled">
                    {breadcrumbs.map((item, i) => {
                        const isLast = i === breadcrumbs.length - 1;
                        const itemAbsoluteUrl = item.href ? absoluteFromHost(host, item.href) : undefined;
                        return (
                            <li
                                key={i}
                                className="breadcrumbs__list-item text-xs"
                                itemProp="itemListElement"
                                itemScope
                                itemType="http://schema.org/ListItem"
                            >
                                {item.href && !isLast ? (
                                    <Link
                                        className="breadcrumbs__link text-caption-sm"
                                        href={item.href}
                                        itemProp="item"
                                    >
                                        <span itemProp="name" className="breadcrumbs__link-item">
                                            <Typografed>{item.text}</Typografed>
                                        </span>
                                    </Link>
                                ) : item.href && isLast ? (
                                    <>
                                        <span className="text-caption-sm breadcrumbs__link">
                                            <span itemProp="name" className="breadcrumbs__link-item">
                                                <Typografed>{item.text}</Typografed>
                                            </span>
                                        </span>
                                        {itemAbsoluteUrl ? <meta itemProp="item" content={itemAbsoluteUrl} /> : null}
                                    </>
                                ) : (
                                    <span className="text-caption-sm breadcrumbs__link" itemProp="item">
                                        <span itemProp="name" className="breadcrumbs__link-item">
                                            <Typografed>{item.text}</Typografed>
                                        </span>
                                    </span>
                                )}
                                <meta itemProp="position" content={`${i + 1}`} />
                            </li>
                        );
                    })}
                </ol>
            </div>
        </nav>
    ) : null;
});

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
